import { computed } from "vue";
import { useRoute } from "vue-router";
import FormsApp from "@/components/FormsApp";
import InterestedStudentsReport from "@/components/InterestedStudentsReport";
import MyStudentDetail from "@/components/MyStudentDetail/MyStudentDetail.vue";
import MyStudents403Forbidden from "@/components/MyStudentDetail/MyStudents403Forbidden.vue";
import MyStudentsAcceptance from "@/components/MyStudents/MyStudentsAcceptance/index.vue";
import MyStudentsApproval from "@/components/MyStudents/MyStudentsApproval.vue";
import MyStudentsDashboard from "@/components/MyStudents/MyStudentsDashboard.vue";
import MyStudentsList from "@/components/MyStudents/MyStudentsList.vue";
import MyStudentsApp from "@/components/MyStudentsApp";
import { ApprovalIcon, ForumIcon, PersonRaisedHand } from "@/components/shared/icons";
import {
  BrowseActivityIcon,
  ContractEditIcon,
  ForYouIcon,
  QueryStatsIcon,
  TodoSubmissionsIcon,
  TodosInboxIcon,
} from "@/components/svg-icons";
import {
  hasAdvisorInstitutionPermission,
  hasAiAdvisingChatbotEnabled,
  hasAnyTableauLevelPermission,
  hasMyStudentsToDosEnabled,
  hasShowStudentCommunicationsEnabled,
  hasTodosInboxReportEnabled,
  hasTodosSubmissionsReportEnabled,
  hasTodoUpdateSubmissionPermission,
  hasTodoViewSubmissionPermission,
  isTenantManager,
  userIsAcceptanceStudentManager,
  userIsApprovalStudentManager,
} from "@/composables/authorization";
import { useExchangeMode } from "@/composables/useExchangeMode";
import { getOnboardingLabel, getOnboardingPage, ONBOARDING_PAGES } from "../utils";

export const useMyStudentsNavigation = () => {
  const route = useRoute();

  const { exchangeMode, userIsInboundStudentManager, userIsOutboundStudentManager } = useExchangeMode();

  const INBOUND = "inbound";
  const OUTBOUND = "outbound";
  const isInbound = computed(() => exchangeMode.value === INBOUND);
  const isOutbound = computed(() => exchangeMode.value === OUTBOUND);

  const showApprovals = computed(
    () => userIsApprovalStudentManager.value && userIsOutboundStudentManager.value && isOutbound.value,
  );

  const showAcceptances = computed(
    () => userIsAcceptanceStudentManager.value && userIsInboundStudentManager.value && isInbound.value,
  );

  const onboardingPage = computed(() => getOnboardingPage(hasMyStudentsToDosEnabled.value));
  const onboardingLabel = computed(() => getOnboardingLabel(hasMyStudentsToDosEnabled.value));

  const constructRouterLinkByRoutePath = (routePath) => {
    const approvalStatusParamValue = routePath === "approvals" ? route.query?.approval_status : undefined;

    return {
      name: "student-management",
      params: {
        ...route.params,
        page: routePath,
      },
      query: {
        ...route.query,
        approval_status: approvalStatusParamValue,
      },
    };
  };

  const sections = computed(() => [
    {
      title: "Home",
      items: [
        {
          icon: BrowseActivityIcon,
          text: "Dashboard",
          routerLink: constructRouterLinkByRoutePath(""),
        },
        {
          icon: ForYouIcon,
          text: "My Students",
          routerLink: constructRouterLinkByRoutePath("listing"),
        },
        showApprovals.value && {
          icon: ApprovalIcon,
          text: "Approvals",
          routerLink: constructRouterLinkByRoutePath("approvals"),
        },
        showAcceptances.value && {
          icon: ApprovalIcon,
          text: "Acceptances",
          routerLink: constructRouterLinkByRoutePath("acceptances"),
        },
        hasShowStudentCommunicationsEnabled.value && {
          icon: ForumIcon,
          text: "Communications",
          href: "/my-students/communications",
        },
      ].filter(Boolean),
    },
    {
      title: "Reporting",
      items: [
        hasAdvisorInstitutionPermission.value &&
          hasAnyTableauLevelPermission.value && {
            icon: PersonRaisedHand,
            text: "Interested Students",
            routerLink: constructRouterLinkByRoutePath("interested-students"),
          },
        {
          icon: ContractEditIcon,
          text: onboardingLabel.value,
          routerLink: constructRouterLinkByRoutePath(onboardingPage.value),
        },
        hasTodosInboxReportEnabled.value &&
          hasTodoUpdateSubmissionPermission.value && {
            icon: TodosInboxIcon,
            text: "To-Dos Inbox",
            href: "/my-students/reporting/forms-inbox/",
          },
        hasTodosSubmissionsReportEnabled.value &&
          hasTodoViewSubmissionPermission.value && {
            icon: TodoSubmissionsIcon,
            text: "To-Do Submissions",
            href: "/my-students/reporting/todos-submissions/",
          },
        {
          icon: QueryStatsIcon,
          text: "Benchmarking",
          routerLink: constructRouterLinkByRoutePath("benchmarking"),
        },
        hasAiAdvisingChatbotEnabled.value &&
          isTenantManager() && {
            muiIconName: "connect_without_contact",
            text: "Anonymous Advising",
            routerLink: {
              name: "my-students-app-anonymous-advising",
            },
          },
      ].filter(Boolean),
    },
  ]);

  const currentPageParam = computed(() => route.params?.page ?? "");

  const title = computed(() => {
    switch (currentPageParam.value) {
      case "approvals":
        return isOutbound.value ? "Approvals for Active Sessions" : "Acceptances for Active Sessions";
      case "acceptances":
        return isInbound.value ? "Acceptances for Active Sessions" : "Approvals for Active Sessions";
      case ONBOARDING_PAGES.legacy:
      case ONBOARDING_PAGES.dashboard:
        return onboardingLabel.value;
      default:
        return "My Students";
    }
  });

  const showSideMenu = computed(() => {
    const hiddenPages = [
      "listing",
      "benchmarking",
      ONBOARDING_PAGES.legacy,
      ONBOARDING_PAGES.dashboard,
      "student-detail",
      "approvals",
      "acceptances",
      "interested-students",
    ];
    return !hiddenPages.includes(currentPageParam.value);
  });

  const currentPage = computed(() => {
    switch (currentPageParam.value) {
      case "listing":
        return MyStudentsList;
      case "benchmarking":
        return MyStudentsApp;
      case ONBOARDING_PAGES.legacy:
      case ONBOARDING_PAGES.dashboard:
        return FormsApp;
      case "student-detail":
        return MyStudentDetail;
      case "approvals":
        return showApprovals.value ? MyStudentsApproval : MyStudents403Forbidden;
      case "acceptances":
        return showAcceptances.value ? MyStudentsAcceptance : MyStudents403Forbidden;
      case "interested-students":
        return InterestedStudentsReport;
      default:
        return MyStudentsDashboard;
    }
  });

  const toastMenu = computed(() => {
    let description = "";
    switch (currentPageParam.value) {
      case "acceptances":
        description = isInbound.value
          ? "Showing students only for sessions in the future or currently still in-progress."
          : "";
        break;
      case "approvals":
        description = isOutbound.value ? "Showing only students for upcoming program sessions." : "";
        break;
      default:
        description = "";
    }
    return {
      show: description.length > 0,
      description,
    };
  });

  return {
    sections,
    title,
    toastMenu,
    showSideMenu,
    currentPage,
  };
};
